import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
  alpha,
  Avatar,
} from "@mui/material";
import CustomButton from "./CustomButton";
import { TenantDashboardData } from "../types";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import AppsIcon from "@mui/icons-material/Apps";
import PeopleIcon from "@mui/icons-material/People";
import DevicesIcon from "@mui/icons-material/Devices";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { useNavigate } from "react-router-dom";

interface TenantDashboardProps {
  data: TenantDashboardData;
}

export default function TenantDashboard({ data }: TenantDashboardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { license, usage, applications } = data;

  const getDaysRemaining = (dateString: string) => {
    if (!dateString) return 0;
    const end = new Date(dateString);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const daysRemaining = getDaysRemaining(license.validUntil);
  const isExpiring = daysRemaining <= 30;
  const isExpired = daysRemaining <= 0;

  const getStatusColor = () => {
    if (license.status !== "active") return theme.palette.error.main;
    if (isExpired) return theme.palette.error.main;
    if (isExpiring) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const LicenseIcon = isExpired
    ? ErrorIcon
    : isExpiring
      ? WarningIcon
      : CheckCircleIcon;

  return (
    <Stack spacing={3}>
      {/* Top Section: License & Quick Actions */}
      <Grid container spacing={3}>
        {/* License Status Card */}
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
              >
                <Box>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight={700}
                  >
                    {t("dashboard.tenant.licenseStatus", "Status da Licença")}
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 1, mb: 0.5 }}
                  >
                    <LicenseIcon
                      sx={{ color: getStatusColor(), fontSize: 28 }}
                    />
                    <Typography variant="h4" fontWeight={600}>
                      {isExpired
                        ? t("dashboard.tenant.expired", "Expirada")
                        : license.isDemo
                          ? t("dashboard.tenant.demo", "Período de Teste")
                          : t("dashboard.tenant.active", "Ativa")}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {license.plan} •{" "}
                    {t("dashboard.tenant.validUntil", "Válida até {{date}}", {
                      date: new Date(license.validUntil).toLocaleDateString(),
                    })}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  {!isExpired && (
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: alpha(getStatusColor(), 0.1),
                        borderRadius: 2,
                        display: "inline-block",
                      }}
                    >
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color={getStatusColor()}
                      >
                        {daysRemaining}
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color={getStatusColor()}
                      >
                        {t("dashboard.tenant.daysRemaining", "Dias Restantes")}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Support Card */}
        <Grid item xs={12} md={4}>
          <Card
            variant="outlined"
            sx={{
              height: "100%",
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <SupportAgentIcon />
                  <Typography variant="h6" fontWeight={600}>
                    {t("dashboard.tenant.support", "Precisa de Ajuda?")}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {t(
                    "dashboard.tenant.supportDesc",
                    "Abra um ticket para resolver problemas ou tirar dúvidas.",
                  )}
                </Typography>
              </Box>
              <CustomButton
                variant="primary"
                onClick={() => navigate("/tickets")}
              >
                {t("dashboard.tenant.openTicket", "Abrir Ticket")}
              </CustomButton>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Usage Section */}
      <Typography variant="h6" fontWeight={600}>
        {t("dashboard.tenant.usage", "Uso de Recursos")}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Avatar
                  sx={{ bgcolor: "action.selected", color: "primary.main" }}
                >
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {usage.users.label}
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {usage.users.current} /{" "}
                    {usage.users.max === 0 ? "∞" : usage.users.max}
                  </Typography>
                </Box>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={
                  usage.users.max > 0
                    ? (usage.users.current / usage.users.max) * 100
                    : 0
                }
                sx={{ height: 8, borderRadius: 4 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: "action.selected", color: "info.main" }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {usage.externalSessions?.label || "Sessões Externas"}
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {usage.externalSessions?.current ?? 0} /{" "}
                    {usage.externalSessions?.max === 0 ||
                    usage.externalSessions?.max === null
                      ? "∞"
                      : usage.externalSessions?.max}
                  </Typography>
                </Box>
              </Stack>
              <LinearProgress
                variant="determinate"
                color="info"
                value={
                  usage.externalSessions?.max && usage.externalSessions.max > 0
                    ? (usage.externalSessions.current /
                        usage.externalSessions.max) *
                      100
                    : 0
                }
                sx={{ height: 8, borderRadius: 4 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Avatar
                  sx={{ bgcolor: "action.selected", color: "secondary.main" }}
                >
                  <DevicesIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {usage.devices.label}
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {usage.devices.current} /{" "}
                    {usage.devices.max === 0 ? "∞" : usage.devices.max}
                  </Typography>
                </Box>
              </Stack>
              <LinearProgress
                variant="determinate"
                color="secondary"
                value={
                  usage.devices.max > 0
                    ? (usage.devices.current / usage.devices.max) * 100
                    : 0
                }
                sx={{ height: 8, borderRadius: 4 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Applications Section */}
      <Typography variant="h6" fontWeight={600}>
        {t("dashboard.tenant.myApps", "Meus Aplicativos")}
      </Typography>
      <Grid container spacing={3}>
        {applications.map((app) => (
          <Grid item xs={12} sm={6} md={4} key={app.id}>
            <Card
              variant="outlined"
              sx={{
                height: "100%",
                transition: "0.3s",
                "&:hover": { boxShadow: 4 },
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={2} mb={2}>
                  <Avatar
                    src={app.logoUrl || undefined}
                    variant="rounded"
                    sx={{ width: 48, height: 48, bgcolor: "primary.light" }}
                  >
                    {!app.logoUrl && <AppsIcon />}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600} lineHeight={1.2}>
                      {app.name}
                    </Typography>
                    <Chip
                      label={app.code}
                      size="small"
                      sx={{ mt: 0.5, height: 20, fontSize: 11 }}
                    />
                  </Box>
                </Stack>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t("dashboard.tenant.modules", "Módulos Ativos:")}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {app.modules.length > 0 ? (
                    app.modules.map((mod, idx) => (
                      <Chip
                        key={idx}
                        label={mod}
                        size="small"
                        variant="outlined"
                      />
                    ))
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      {t("common.none", "Nenhum")}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}
